import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import Spinner from '../../../../components/Spinner';
import { openModal } from '../../../../../data/reducers/modalReducer';
import { formatNumber } from './queueMonitorFormat';

/**
 * RetryHotspots
 * Top-10 properties by job executions, from the EXISTING /top-properties endpoint
 * (zero backend work). Properties with abnormally many executions are the retry
 * hotspots dragging the "job runs per property" mean up.
 *
 * Row click opens the existing property-stats modal, exactly like PropertyAddressCell
 * does (MODAL_TYPES.PROPERTY_STATS with { property: { id, address } }).
 */

// Value of MODAL_TYPES.PROPERTY_STATS (src/view/ModalManager.js). Imported by value
// rather than by reference: ModalManager's import graph uses webpack-only path aliases
// (jsconfig paths) that the jest config cannot resolve, which would make any component
// importing it untestable. The string is a stable identifier.
const PROPERTY_STATS_MODAL_TYPE = 'PROPERTY_STATS';

const HEADER_TOOLTIP =
	'Properties with abnormally many job runs are the retry hotspots dragging the mean up';

const RetryHotspots = ({ topProperties, isLoading }) => {
	const dispatch = useDispatch();

	const header = (
		<h4 className="retry-hotspots-header" title={HEADER_TOOLTIP}>
			Retry hotspots
			<i
				className="bi bi-info-circle ms-2 text-muted small"
				role="img"
				aria-label="About retry hotspots"
			/>
		</h4>
	);

	if (isLoading) {
		return (
			<div className="retry-hotspots">
				{header}
				<div className="text-center py-3">
					<Spinner />
				</div>
			</div>
		);
	}

	const rows = Array.isArray(topProperties) ? topProperties : [];

	if (rows.length === 0) {
		return (
			<div className="retry-hotspots">
				{header}
				<p className="text-muted retry-hotspots-empty">No property job stats yet</p>
			</div>
		);
	}

	const handleRowClick = (row) => {
		dispatch(
			openModal(PROPERTY_STATS_MODAL_TYPE, {
				property: {
					id: row.propertyId,
					address: row.address,
				},
			}),
		);
	};

	return (
		<div className="retry-hotspots">
			{header}
			<div className="table-responsive">
				<table className="table table-sm table-hover">
					<thead className="table-light">
						<tr>
							<th>Address</th>
							<th className="text-end">Executions</th>
							<th className="text-end">Failures</th>
							<th className="text-end">BIS</th>
							<th className="text-end">OD</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row, index) => (
							<tr
								key={row.propertyId !== undefined ? row.propertyId : index}
								className="retry-hotspot-row"
								style={{ cursor: 'pointer' }}
								title="View property statistics"
								onClick={() => handleRowClick(row)}
							>
								<td>{row.address}</td>
								<td className="text-end">{formatNumber(row.totalExecutions)}</td>
								<td className="text-end">{formatNumber(row.totalFailures)}</td>
								<td className="text-end">{formatNumber(row.bisExecutions)}</td>
								<td className="text-end">{formatNumber(row.odExecutions)}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
};

RetryHotspots.propTypes = {
	topProperties: PropTypes.array,
	isLoading: PropTypes.bool,
};

RetryHotspots.defaultProps = {
	topProperties: undefined,
	isLoading: false,
};

export default RetryHotspots;
